import React, { useState, useEffect } from "react";
import { useTranslation } from "./translations/useTranslation";
import { useNavigate, useParams } from "react-router-dom";
import ReactLoading from "react-loading";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Stack,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  useToast,
  useMediaQuery,
  VStack,
  HStack,
  Icon
} from "@chakra-ui/react";
import { FaCheck, FaImage, FaMapMarkerAlt, FaInfoCircle, FaUser, FaDollarSign } from "react-icons/fa";
import Sell from "./Sell";
import MultipleImageUploadComponent from "./SellFormComponents/multiple-image-upload.component";
import Price from "./SellFormComponents/Price";
import SelectLocation from "./SellFormComponents/SelectLocation";
import Details from "./SellFormComponents/Details";
import Addetails from "./SellFormComponents/Addetails";
import axios from "axios";
import { categories } from "./resources/Catagories";
import NotFound from "./resources/NotFound";
import { API_BASE_URL } from "./config/api_keys";

// Updated steps array - removed profile step
const steps = [
  { title: 'product', description: 'productDetails', icon: FaInfoCircle },
  { title: 'price', description: 'setYourPrice', icon: FaDollarSign },
  { title: 'location', description: 'whereToSell', icon: FaMapMarkerAlt },
  { title: 'images', description: 'uploadPhotos', icon: FaImage },
];

export default function SellForm() {
  const { t } = useTranslation();
  const { category } = useParams();
  const { item } = useParams();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  // Start from step 0 (which is now product details) instead of step 0 (profile)
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  // Validate category and item
  const isValidCategory = categories.some(
    (cat) => cat.title.toLowerCase() === category.toLowerCase()
  );
  const isValidItem = categories.some((cat) => cat.items.includes(item));

  const [showComponent, setShowComponent] = useState(false);
  const handleClick = () => {
    setShowComponent(!showComponent);
  };

  const [formInputs, setFormInputs] = useState([]);

  const handleAddForm = () => {
    if (formInputs.length < 12) {
      // check if the length of formInputs is less than 12
      setFormInputs((prevFormInputs) => [
        ...prevFormInputs,
        <MultipleImageUploadComponent
          key={prevFormInputs.length}
          onFileSelect={(file) => handleFileChange(file)}
        />,
      ]);
    }
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [price, setPrice] = useState();
  const [priceUnit, setPriceUnit] = useState(""); // Initialize empty and set based on category
  const [location, setLocation] = useState({});
  const [address, setaddress] = useState({});
  const [addorloc, setaddorloc] = useState("location");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState();
  const [name, setName] = useState();
  // Agriculture-specific states
  const [agricultureDetails, setAgricultureDetails] = useState({
    quantity: "",
    unit: "",
    cultivationMethod: "",
    harvestDate: "",
    quality: "",
    variety: "",
    age: "",
    breed: ""
  });
  const toast = useToast();

  // Set default units based on category and populate name/image from localStorage
  useEffect(() => {
    // Default unit based on category
    let defaultUnit = "kg";
    let defaultPriceUnit = "kg";
    
    if (category === "Grains" || category === "Vegetables" || category === "Spices") {
      defaultUnit = "kg";
      defaultPriceUnit = "kg";
    } else if (category === "Fruits") {
      defaultUnit = "kg";
      defaultPriceUnit = "kg";
    } else if (category === "Dairy") {
      defaultUnit = "liter";
      defaultPriceUnit = "liter";
    } else if (category === "Livestock") {
      defaultUnit = "piece";
      defaultPriceUnit = "piece";
    }
    
    setAgricultureDetails(prev => ({
      ...prev,
      unit: defaultUnit
    }));
    
    setPriceUnit(defaultPriceUnit);
    
    // Automatically populate name and image from localStorage
    const authname = localStorage.getItem("authname");
    const authpicture = localStorage.getItem("authpicture");
    if (authname) setName(authname);
    if (authpicture) setImage(authpicture);
  }, [category]);

  const handleFileChange = (file) => {
    // Handle the uploaded file here
    console.log("File selected:", file);
    console.log("File type:", file.type);
    console.log("File size:", file.size);
    
    // Test upload to see if it works
    const testUpload = async () => {
      try {
        const formData = new FormData();
        formData.append("image", file);
        const response = await axios.post(`${API_BASE_URL}/test-upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        console.log("Test upload successful:", response.data);
      } catch (error) {
        console.error("Test upload failed:", error);
      }
    };
    testUpload();
    
    setUploadedFiles((prevUploadedFiles) => [...prevUploadedFiles, file]);
  };
  
  const handlePriceChange = (price) => {
    // Handle the uploaded Price here
    setPrice(price);
  };
  
  const handleUnitPriceChange = (unit) => {
    // Handle the price unit change
    setPriceUnit(unit);
  };

  // Update the handleAddressChange function to match the new SelectLocation component
  const handleAddressChange = (data) => {
    // Extract location, address, addorloc, and coordinates from the data object
    const { location = {}, address = {}, addorloc = "location", coordinates = null } = data || {};
    
    // Ensure we have objects even if they're undefined
    setLocation(location || {});
    setaddress(address || {});
    setaddorloc(addorloc || "location");
    
    // Store coordinates if available
    if (coordinates) {
      setLocation(prev => ({
        ...prev,
        coordinates
      }));
    }
    
    console.log("Location updated:", location);
    console.log("Address updated:", address);
    console.log("AddOrLoc updated:", addorloc);
    console.log("Coordinates:", coordinates);
  };

  const handleDetailsChange = ({ title, description }) => {
    // Handle the Details here
    if (title !== undefined) {
      setTitle(title);
    }
    if (description !== undefined) {
      setDescription(description);
    }
    
    console.log("Title updated:", title);
    console.log("Description updated:", description);
  };

  const handleAgricultureDetailsChange = (details) => {
    setAgricultureDetails(details);
  };

  function handleNameSelect(name) {
    setName(name);
  }
  
  function handleImageSelect(img) {
    setImage(img);
  }

async function handleFormSubmit(event) {
  event.preventDefault();

  console.log("Form submission - checking values:");
  console.log("Title:", title);
  console.log("Description:", description);
  console.log("Address:", address);
  console.log("Location:", location);
  console.log("AddOrLoc:", addorloc);
  console.log("Price:", price);
  console.log("Files:", uploadedFiles.length);
  console.log("Name:", name);
  console.log("Quantity:", agricultureDetails.quantity);

  const errors = [];

  if (!title || title.trim() === "") {
    errors.push("Please add title");
  }
  
  if (!description || description.trim() === "") {
    errors.push("Please add description");
  }
  
  // Check if we have both address and location coordinates
  // Improved validation to properly check address fields from both manual and automatic inputs
  const hasValidAddress = (address && address.state && address.city) || 
                          (location && location.state && location.city);
  
  if (!hasValidAddress) {
    errors.push("Please add complete address details (state and city are required)");
  }
  
  // Check for location coordinates
  const hasCoordinates = location && location.coordinates && 
                        location.coordinates.latitude && location.coordinates.longitude;
  
  if (!hasCoordinates) {
    errors.push("Please select location coordinates on the map");
  }
  
  if (uploadedFiles.length === 0) {
    errors.push("Please upload at least one product image");
    toast({
      title: "Image Required",
      description: "At least one product image is required",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  
  if (!name) {
    errors.push("Please add your name");
  }
  
  if (!price) {
    errors.push("Please provide a price");
  }
  
  if (!agricultureDetails.quantity) {
    errors.push("Please provide quantity");
  }

  if (errors.length > 0) {
    toast({
      title: "Error",
      description: errors.join("\n"),
      status: "error",
      duration: 5000,
      isClosable: true,
    });

    return;
  }
  
  setLoading("post");

  try {
    const [imageUrl, picUrls] = await Promise.all([
      image
        ? (async () => {
            try {
              console.log("Uploading profile image:", image);
              
              // Check if image is a File object or a URL string
              if (image instanceof File) {
                const formData = new FormData();
                formData.append("image", image);
                const token = localStorage.getItem('authToken');
                console.log("FormData entries:", Array.from(formData.entries()));
                const { data } = await axios.post(
                  `${API_BASE_URL}/upload-product-image`,
                  formData,
                  {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  }
                );
                return data.imageUrl;
              } else {
                // If image is a URL string, return it as is
                console.log("Profile image is already a URL:", image);
                return image;
              }
            } catch (error) {
              console.error("Error uploading profile image:", error);
              throw new Error("Failed to upload profile image");
            }
          })()
        : "",
      uploadedFiles.length > 0
        ? Promise.all(
            uploadedFiles.map(async (file, index) => {
              try {
                console.log(`Uploading product image ${index + 1}:`, file);
                const formData = new FormData();
                formData.append("image", file);
                const token = localStorage.getItem('authToken');
                console.log(`FormData entries for image ${index + 1}:`, Array.from(formData.entries()));
                const { data } = await axios.post(
                  `${API_BASE_URL}/upload-product-image`,
                  formData,
                  {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  }
                );
                return data.imageUrl;
              } catch (error) {
                console.error(`Error uploading product image ${index + 1}:`, error);
                throw new Error(`Failed to upload product image ${index + 1}`);
              }
            })
          )
        : [],
    ]);
    
    const token = localStorage.getItem('authToken');
    
    // Always use address for human-readable location
    const locationData = address;
    
    // Prepare coordinates in GeoJSON format
    let geoLocation = null;
    if (location.coordinates) {
      // Ensure coordinates are properly formatted for GeoJSON
      const longitude = parseFloat(location.coordinates.longitude);
      const latitude = parseFloat(location.coordinates.latitude);
      
      geoLocation = {
        type: "Point",
        coordinates: [longitude, latitude]
      };
      
      console.log("Formatted GeoJSON location:", geoLocation);
    } else {
      console.error("No coordinates available in location data");
      throw new Error("Location coordinates are required");
    }
    
    console.log("Sending data to server:", {
      title,
      description,
      address: locationData,
      price,
      priceUnit,
      uploadedFiles: picUrls,
      image: imageUrl,
      name,
      catagory: category,
      subcatagory: item,
      quantity: agricultureDetails.quantity,
      unit: agricultureDetails.unit,
      location: geoLocation,
      agricultureDetails,
      username: name // Set username to name for validation
    });
    
    // Add store reference if selected
    const productData = {
      title,
      description,
      address: locationData,
      price,
      priceUnit,
      uploadedFiles: picUrls,
      image: imageUrl,
      name,
      username: name, // Add username field (required by Product model)
      catagory: category,
      subcatagory: item,
      // Agriculture-specific fields
      quantity: agricultureDetails.quantity,
      unit: agricultureDetails.unit,
      cultivationMethod: agricultureDetails.cultivationMethod,
      harvestDate: agricultureDetails.harvestDate,
      quality: agricultureDetails.quality,
      variety: agricultureDetails.variety,
      age: agricultureDetails.age,
      breed: agricultureDetails.breed,
      location: geoLocation // Add geoLocation to the request
    };
    
    // Add store reference if it exists in agricultureDetails
    if (agricultureDetails.store && agricultureDetails.store !== "") {
      console.log("Adding store reference to product:", agricultureDetails.store);
      productData.store = agricultureDetails.store;
    } else {
      console.log("No store selected for this product");
    }
    
    await axios.post(`${API_BASE_URL}/add_product`, productData, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    setLoading("redirect");
    navigate('/myads');

  } catch (error) {
    console.error("Product submission error:", error);
    
    let errorMessage = "Failed to add product. Please try again.";
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      if (status === 400) {
        errorMessage = (typeof data.error === 'string') ? data.error : "Invalid data provided. Please check your inputs.";
      } else if (status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
        // Redirect to login
        localStorage.removeItem('authToken');
        navigate('/login');
      } else if (status === 500) {
        errorMessage = (typeof data.error === 'string') ? data.error : "Server error. Please try again later.";
      } else {
        errorMessage = `Server error (${status}). Please try again.`;
      }
    } else if (error.request) {
      // Network error
      errorMessage = "Network error. Please check your internet connection.";
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    setLoading("");
  }
}

  // Render form components based on active step (adjusted for removed profile step)
  const renderStepContent = (step) => {
    switch (step) {
      // case 0 was profile, now case 0 is product details
      case 0:
        return <Details
                onTitleSelect={handleDetailsChange}
                onDescriptionSelect={handleDetailsChange}
                onAgricultureDetailsChange={handleAgricultureDetailsChange}
                category={category}
              />;
      case 1:
        return <Price 
                onPriceSelect={handlePriceChange} 
                onUnitPriceSelect={handleUnitPriceChange}
                category={category}
                initialUnit={priceUnit}
              />;
      case 2:
        return <SelectLocation onAddressSelect={handleAddressChange} />;
      case 3:
        return (
          <Box>
            <Heading as="h5" size="sm" mb={4}>{t('uploadPhotos')}</Heading>
            <MultipleImageUploadComponent onFileSelect={handleFileChange} />
            {formInputs.map((input, index) => (
              <Box key={index} mt={3}>{input}</Box>
            ))}
            <Button 
              mt={4} 
              colorScheme="green" 
              onClick={handleAddForm}
              isDisabled={formInputs.length >= 12}
            >
              {t('addMoreImages')}
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {isValidCategory && isValidItem ? (
        <Container maxW={isMobile ? "container.sm" : "container.md"} py={6}>
          {loading === "post" ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              minHeight="60vh"
              flexDirection="column"
            >
              <ReactLoading type="spin" color="green" height={"50px"} width={"50px"} />
              <Text mt={4} fontSize="lg">{t('uploadingYourProduct')}</Text>
            </Box>
          ) : loading === "redirect" ? (
            navigate("/myads")
          ) : (
            <VStack spacing={6}>
              <Card w="100%" overflow="hidden" boxShadow="md" borderRadius="lg">
                <CardHeader bg="green.500" color="white" py={4}>
                  <Heading size="md" textAlign="center">
                    {category} / {item}
                  </Heading>
                </CardHeader>
                
                <CardBody p={0}>
                  {/* Stepper for desktop */}
                  {!isMobile && (
                    <Box px={6} py={4}>
                      <Stepper index={activeStep} colorScheme="green">
                        {steps.map((step, index) => (
                          <Step key={index} onClick={() => setActiveStep(index)} cursor="pointer">
                            <StepIndicator>
                              <StepStatus
                                complete={<Icon as={step.icon} />}
                                incomplete={<StepNumber />}
                                active={<Icon as={step.icon} />}
                              />
                            </StepIndicator>
                            <Box flexShrink="0">
                              <StepTitle>{t(step.title)}</StepTitle>
                              <StepDescription>{t(step.description)}</StepDescription>
                            </Box>
                            <StepSeparator />
                          </Step>
                        ))}
                      </Stepper>
                    </Box>
                  )}
                  
                  {/* Mobile step indicator */}
                  {isMobile && (
                    <HStack justify="space-between" p={4} bg="gray.50">
                      {steps.map((step, index) => (
                        <VStack 
                          key={index} 
                          onClick={() => setActiveStep(index)}
                          cursor="pointer"
                          opacity={activeStep === index ? 1 : 0.6}
                          color={activeStep === index ? "green.500" : "gray.500"}
                        >
                          <Icon 
                            as={step.icon} 
                            boxSize={6} 
                            bg={activeStep === index ? "green.100" : "transparent"}
                            p={1}
                            borderRadius="full"
                          />
                          <Text fontSize="xs" display={{ base: "none", sm: "block" }}>
                            {t(step.title)}
                          </Text>
                        </VStack>
                      ))}
                    </HStack>
                  )}
                  
                  <Divider />
                  
                  {/* Step content */}
                  <Box p={6}>
                    {renderStepContent(activeStep)}
                  </Box>
                  
                  <Divider />
                  
                  {/* Navigation buttons */}
                  <Box p={4} bg="gray.50">
                    <HStack justifyContent="space-between">
                      <Button
                        isDisabled={activeStep === 0}
                        onClick={() => setActiveStep(activeStep - 1)}
                        variant="outline"
                        colorScheme="green"
                      >
                        {t('previous')}
                      </Button>
                      
                      {activeStep < steps.length - 1 ? (
                        <Button
                          onClick={() => setActiveStep(activeStep + 1)}
                          colorScheme="green"
                        >
                          {t('next')}
                        </Button>
                      ) : (
                        <Button
                          colorScheme="green"
                          onClick={handleFormSubmit}
                          leftIcon={<FaCheck />}
                        >
                          {t('submit')}
                        </Button>
                      )}
                    </HStack>
                  </Box>
                </CardBody>
              </Card>
            </VStack>
          )}
        </Container>
      ) : (
        <NotFound />
      )}
    </div>
  );
}